import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const START_PROGRESS = 8;
const MAX_PROGRESS = 95;
const PROGRESS_INTERVAL_MS = 100;
const COMPLETION_DELAY_MS = 250;

export function NavigationProgress() {
  const { t } = useTranslation();
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const isActiveRef = useRef(false);
  const intervalRef = useRef<number | null>(null);
  const completionTimeoutRef = useRef<number | null>(null);

  const clearProgressTimers = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (completionTimeoutRef.current !== null) {
      window.clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
  }, []);

  const startProgress = useCallback(() => {
    clearProgressTimers();
    isActiveRef.current = true;
    progressRef.current = START_PROGRESS;
    setIsVisible(true);
    setProgress(START_PROGRESS);

    intervalRef.current = window.setInterval(() => {
      const current = progressRef.current;
      const increment = current < 30 ? 8 : current < 60 ? 3 : current < 80 ? 1 : 0.2;
      const next = Math.min(current + increment, MAX_PROGRESS);
      progressRef.current = next;
      setProgress(next);
    }, PROGRESS_INTERVAL_MS);
  }, [clearProgressTimers]);

  const completeProgress = useCallback(() => {
    if (!isActiveRef.current) return;
    clearProgressTimers();
    isActiveRef.current = false;
    progressRef.current = 100;
    setProgress(100);
    completionTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
      setProgress(0);
      progressRef.current = 0;
      completionTimeoutRef.current = null;
    }, COMPLETION_DELAY_MS);
  }, [clearProgressTimers]);

  useEffect(() => {
    const unsubscribeBeforeNavigate = router.subscribe("onBeforeNavigate", startProgress);
    const unsubscribeResolved = router.subscribe("onResolved", completeProgress);

    return () => {
      unsubscribeBeforeNavigate();
      unsubscribeResolved();
      clearProgressTimers();
    };
  }, [clearProgressTimers, completeProgress, router, startProgress]);

  if (!isVisible) return null;

  return (
    <>
      <div
        aria-label={t("common:pageLoading")}
        className="pointer-events-none fixed inset-x-0 top-0 z-navigation-progress h-0.5 bg-transparent"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-action-primary transition-[width] duration-150 ease-out motion-reduce:transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="sr-only" role="status">
        {t("common:pageLoading")}
      </span>
    </>
  );
}
